import React, { PureComponent } from 'react';
import { Flex } from 'app/components/common/Flex';
import { MeasureDto } from 'app/model/CORE';
import { CheckCircleFilled, FileFilled, StarFilled } from '@ant-design/icons';
import { MeasureLoadBar } from './svg/MeasureLoadBar';
import { EntryLoadBar } from './svg/EntryLoadBar';
import { OccupancyLoadBar } from './svg/OccupancyLoadBar';
import { ExtendedMonitoredTv, MonitoredEvent, MonitoredMeasure } from 'app/model/SOL49';
import MeasureUtils from 'app/common/util/MeasureUtils';
import './MesureLoad.less';
import { MeasureWarnings } from 'app/components/dashboard/detailed/measures/MeasureWarning';
import { MeasureMoreMenu } from 'app/components/dashboard/detailed/measures/MeasureMoreMenu';
import ConditionalRender from 'app/common/components/ConditionalRender';
import AppContext from 'app/common/context/AppContext';
import Utils from 'app/common/util/Utils';
import { OpenTvMonitoredEvents } from 'app/common/eventbus/Events';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import AppConstants from '../../../../common/context/AppConstants';

export interface MeasureLoadProps {
    monitoredMeasure: MonitoredMeasure;
    monitoredEvents?: Array<MonitoredEvent>;
    currentTime: Date;
    monitoredTv: ExtendedMonitoredTv;
    hasOngoingProposal: boolean;
    hideMeasureId?: boolean; //hides measure name label
    hideSelectIcon?: boolean; //hides selection icon
    hideMoreMenu?: boolean;

    selected?: boolean;
    onSelectionChange?(selected: boolean): void;
}

interface MeasureLoadState {
    animated: boolean;
}

export class MeasureLoad extends PureComponent<MeasureLoadProps, MeasureLoadState> {
    constructor(props) {
        super(props);

        this.state = {
            animated: false,
        };
    }

    componentDidMount() {
        if (this.props.hasOngoingProposal) {
            this.hasBeenAnimated();
        }
    }

    componentDidUpdate(prevProps: Readonly<MeasureLoadProps>, prevState: Readonly<MeasureLoadState>, snapshot?: any) {
        if (this.props.hasOngoingProposal && !prevState.animated) {
            this.hasBeenAnimated();
        } else if (!this.props.hasOngoingProposal && prevState.animated) {
            // No more pending proposal, reset animation state in order to animate it "again" when new proposal arrives
            this.setState({
                animated: false,
            });
        }
    }

    private hasBeenAnimated = () => {
        // prevent animation from restarting ! Adding delay to let current animation finish
        setTimeout(
            () =>
                this.setState({
                    animated: true,
                }),
            15000,
        );
    };

    handleSelectionChange = () => {
        // If selected is undefined, it means that the component cannot be selected
        this.props.selected !== undefined && this.props.onSelectionChange?.(!this.props.selected);
    };

    handleOpenEvent = (event: React.MouseEvent) => {
        event.stopPropagation();
        const measure = this.props.monitoredMeasure.measure;
        const { id, refLocation, tvSetIds } = MeasureUtils.getTvLocation(measure);
        OpenTvMonitoredEvents.fire(
            MonitoredEventUtils.getType(this.props.monitoredEvents?.[0]),
            id,
            refLocation,
            tvSetIds,
            [measure.measureId.id],
        );
    };

    render() {
        //let monitoredPeriod = MonitoredLoadUtils.getMonitoredPeriod(this.props.measureLoad)
        const { measure, measureLoad, warnings } = this.props.monitoredMeasure;
        const monitoredPeriod = AppContext.getTodayPeriod();
        const className = 'measure on-hover' + (this.props.selected ? ' selected' : '');
        const selectorClassName = (this.props.selected !== undefined ? 'show-on-hover' : 'hide') + ' check-circle';
        const regulationState = measure && MeasureUtils.getRegulationState(measure);
        const measureCreationClassname = MeasureUtils.getMonitoredMeasureCreationClassname(this.props.monitoredMeasure);
        const measureLoadBarClassname = `measure-loadbar ${regulationState?.toLowerCase()} ${measureCreationClassname} ${this.props.hasOngoingProposal && !this.state.animated ? 'blink-new-proposal' : ''}`;

        return (
            <Flex className={className} direction="vertical" onClick={this.handleSelectionChange}>
                <Flex>
                    <ConditionalRender condition={!this.props.hideSelectIcon}>
                        <div className={selectorClassName}>
                            <CheckCircleFilled />
                        </div>
                    </ConditionalRender>
                    <ConditionalRender condition={!this.props.hideMeasureId}>
                        <Flex className="measure-id">
                            {measure?.measureId?.id}
                            <ConditionalRender condition={this.props.hasOngoingProposal}>
                                <StarFilled className="star" />
                            </ConditionalRender>
                            <ConditionalRender condition={Utils.isNotEmpty(this.props.monitoredEvents)}>
                                <FileFilled style={{ paddingLeft: '0.5em' }} onClick={this.handleOpenEvent} />
                            </ConditionalRender>
                        </Flex>
                    </ConditionalRender>
                    <MeasureWarnings warnings={warnings} measureId={measure.measureId} />
                </Flex>
                <Flex className={measureLoadBarClassname}>
                    <Flex direction="vertical">
                        <MeasureLoadBar
                            key="regulation-load"
                            loadValues={measureLoad}
                            drawBottomLine={false}
                            monitoredPeriod={monitoredPeriod}
                            currentTime={this.props.currentTime}
                            barHeightEm={AppConstants.measureLoadBarHeightEm}
                        />
                        {this.props.monitoredTv?.loadValues && (
                            <EntryLoadBar
                                key="entry-load"
                                loadValues={this.props.monitoredTv.loadValues}
                                monitoredPeriod={monitoredPeriod}
                                activePeriods={this.props.monitoredTv.activePeriods}
                                currentTime={this.props.currentTime}
                                drawBottomLine={true}
                                barHeightEm={0.5}
                            />
                        )}
                        {this.props.monitoredTv?.occupancyLoadValues &&
                            this.props.monitoredTv?.occupancyLoadValues.map(occupancyValues => {
                                return (
                                    <OccupancyLoadBar
                                        key={'occupancy-load-' + occupancyValues.durationInMin}
                                        loadValues={occupancyValues.occupancyLoadValues}
                                        monitoredPeriod={monitoredPeriod}
                                        activePeriods={this.props.monitoredTv.activePeriods}
                                        currentTime={this.props.currentTime}
                                        drawBottomLine={true}
                                        barHeightEm={0.5}
                                    />
                                );
                            })}
                    </Flex>
                    {this.renderMoreMenu(measure, selectorClassName, this.props.monitoredEvents)}
                </Flex>
            </Flex>
        );
    }

    renderMoreMenu(measure: MeasureDto, selectorClassName: string, events: Array<MonitoredEvent>) {
        if (this.props.hideMoreMenu) return null;
        return (
            <div className={'more-button ' + selectorClassName} onClick={e => e.stopPropagation()}>
                <MeasureMoreMenu
                    measureId={measure.measureId}
                    applicability={measure.applicability}
                    tvLoc={MeasureUtils.getTvLocation(measure)}
                    events={events}
                />
            </div>
        );
    }
}
